import "./suggest_formula";
import {getJsDateFromExcel, methodsWithCategories} from "../mathematics/methods";
import {getType} from "../operations/types";
import {getDateEditFormat} from "../operations/formats";
import {getLastCharIndex} from "../mathematics/parser";
import {getSuggestPos} from "../helpers/suggest_pos";
import {getHighlightValue, removeOldHighlight} from "../helpers/highlight";

webix.protoUI({
	name: "live-editor",
	$cssName:"texthighlight",
	$init(config){
		const master = this.getTopParentView();

		this.paramsPopup = webix.ui({
			view: "popup",
			width: 350,
			maxHeight: 300,
			body:{
				scroll: "auto",
				autoheight: true,
				template: obj => {
					let category = methodsWithCategories[obj.method] || "common";
					category = webix.i18n.spreadsheet.liveEditor[category] || category;
					let html = `<p><span class = "webix_ssheet_method_name">${obj.method}</span><span class="webix_ssheet_method_category">${category}</span></p>`;

					const params = obj.params;
					for(let i = 0; i < params.length; i++){
						const param = `<span class = "webix_ssheet_method_param_name">${params[i][0]+": "}</span>`;
						html += `<p${obj.number == i ? " class = 'webix_ssheet_method_param_active'":""}>${param}<span class="webix_ssheet_method_param_description">${params[i][1]}</p>`;
					}

					return html;
				}
			}
		});
		master._destroy_with_me.push(this.paramsPopup);

		config.suggest = {
			width: 350,
			height: 200,
			view: "suggest-formula",
			css: "webix_ssheet_math_suggest",
			data: config.suggestData,
			template: obj => obj.pos ? obj.id : obj.value,
			body: {
				template: obj => {
					if(obj.id.indexOf("$range_") == 0)
						return `<span class="webix_ssheet_left">${obj.value}</span><span class="webix_ssheet_range webix_ssheet_right">${obj.pos}</span>`;
					else
						return obj.value;
				}
			},
			on: {
				onBeforeShow: function(){
					const list = this.getList();

					const oldRanges = list.find(obj => obj.id.indexOf("$range_") == 0);
					if(oldRanges)
						oldRanges.forEach(obj => list.remove(obj.id));

					const allRanges = master.ranges.serialize(master.getActiveSheet()).map(r => ({
						id: "$range_" + r[0],
						value: r[0],
						pos: r[1],
						global: r[2]
					}));

					const suggestRanges = allRanges.filter(range => !range.global);
					
					allRanges.filter(range => range.global).forEach(range => {
						if(!suggestRanges.find(sRange => sRange.value == range.value))
							suggestRanges.push(range);
					});

					suggestRanges.forEach(range => list.add(range));

					list.filter(item => this.config.filter.apply(this, [item, this.getMasterValue()]));
					list.sort("value", "asc", "string");
				}
			}
		};
		config.highlight = this.highlight;

		this._highlightedCells = {};
		this.$view.className += " webix_ssheet_formula";

		this.attachEvent("onKeyPress", (code, e) => {
			delete this._update_range;
			if(code == 13) {
				//ignore Enter key if it was pressed to select some value from suggest
				if ((new Date() - (this._last_value_set || 0)) > 300){
					const cell = this.config.activeCell;
					this.showActiveSheet(true);
					this.updateCellValue(cell);
					//blur to avoid calling highlight function after selection move
					this.getInputNode().blur();
					//we have 'enter' hot key to activate editor
				}
				webix.html.preventEvent(e);
			}
			//esc - cancel editing
			else if(code == 27){
				this.showActiveSheet();

				webix.delay(()=>{
					const cell = master.getSelectedId();
					const oldVal = this.getCellValue(cell.row, cell.column);

					if(this.getValue() == oldVal)
						master.$$("cells").refresh(cell.row);
					else
						this.setValue(oldVal);

					delete master.$handleSelection;

					webix.UIManager.setFocus(master.$$("cells"));
				});
			}
			else if(code >= 33 && code <= 40) // arrow navigation
				this.showParamsPopup();
		});

		// caret move
		this.attachEvent("onItemClick", function(){
			this.showParamsPopup();
		});

		this.attachEvent("onAfterRender", function(){
			webix.eventRemove(this.keydownEv);

			this.keydownEv = webix.event(this.getInputNode(), "keydown", e => {
				this.endEdit(e);
			});
		});
	},
	showParamsPopup(){
		webix.delay(()=>{
			const popup = this.paramsPopup;
			const text = this.getValue();

			if(text[0] == "="){
				const cursor = this.getInputNode().selectionStart;

				let number = 0;
				let brackets = 0;
				for(let i = cursor-1; i > 0; i--){
					switch (text[i]){
						case ",":
							if(!brackets)
								number++;
							break;
						case ")":
							brackets++;
							break;
						case "(":
							if(!brackets){
								const str = text.substring(0, i);
								let method = str.substring(getLastCharIndex(str, true));
								if(method){
									method = method.toUpperCase();
									const params = webix.i18n.spreadsheet.liveEditor["functions-syntax"][method];
									if(params && params.length){
										const template = popup.getBody();
										template.setValues({
											params,
											number,
											method
										});

										const suggest = webix.$$(this.config.suggest);

										let pos;
										if(suggest.isVisible()){
											const suggestConfig = suggest.config;
											pos = {
												x: suggestConfig.left,
												y: suggestConfig.top + suggest.$height + 10
											};
										}
										else
											pos = getSuggestPos(this);

										popup.show(pos);

										const activeParam = template.$view.querySelector(".webix_ssheet_method_param_active");
										if(activeParam){
											const scrollTop = activeParam.offsetTop + activeParam.offsetHeight - template.$height;
											template.scrollTo(0, scrollTop > 0 ? scrollTop : 0);
										}

										this.focus();
									}
									else
										popup.hide();
								}
								return;
							}
							else
								brackets--;
					}
				}
			}
			popup.hide();
		});
	},
	highlight(value){
		this.showParamsPopup();

		const master = this.getTopParentView();

		removeOldHighlight(master);
		const html = getHighlightValue(master, value, this._activeMath, true);
		master.$$("cells").refresh();

		if(document.activeElement == this.getInputNode())
			this.paintValue();
		return html;
	},
	showActiveSheet(enter){
		const master = this.getTopParentView();
		const grid = master._table;

		if(this._activeMath){
			if(this._activeMath != master.getActiveSheet()){
				const cell = this.config.activeCell;

				this.define({activeCell:null});

				master.showSheet(this._activeMath);

				webix.delay(()=>{
					grid.select(cell.row + (enter ? 1 : 0), cell.column);
				});
			}
			delete this._activeMath;
		}
		else if(enter)
			webix.delay(()=> grid.moveSelection("down"));
	},
	endEdit(e){
		const code = e.which || e.keyCode;

		//if it is a formula - force user to finish it by click outside or 'enter'
		if(code == 9){
			webix.html.preventEvent(e);

			this.getInputNode().blur();
			this.updateCellValue();
			this.getTopParentView()._table.moveSelection(e.shiftKey ? "left" : "right");
		}
	},
	paintValue(){
		const master = this.getTopParentView();
		if(!this._activeMath || this._activeMath == master.getActiveSheet()){
			const cell = this.config.activeCell;
			const node = master._table.getItemNode(cell);
			if(cell && node)
				node.innerHTML = `<div>${getHighlightValue(master, this.getValue())}</div>`;
		}
	},
	updateCellValue(cell) {
		const newv = this.getValue();
		const master = this.getTopParentView();
		cell = cell || this.config.activeCell;

		if (!cell)
			this.setValue("");
		else{
			const val = master.getCellValue(cell.row, cell.column);

			if(newv !== val){
				this.config.value = newv;
				master.setCellValue(cell.row, cell.column, newv);
				master.refresh();
			}
		}

		delete master.$handleSelection;
	},
	$setValueHere(value){
		this.setValueHere(value);
	},
	setValueHere(value) {
		this._last_value_set = new Date();

		const formula = this.getValue();
		if(formula && formula.charAt(0)==="="){
			const cursor = this.getInputNode().selectionStart;
			let str1 = formula.substring(0, cursor);
			const str2 = formula.substring(cursor);

			if(str1[str1.length-1] == "(")
				str1 = str1.substring(0, str1.length-1);

			str1 = str1.substring(0, getLastCharIndex(str1, true));

			const range = value.indexOf("$range_") == 0;
			if(range)
				value = value.substr(7);

			//suggest called via up/down key
			str1 += value + (range || str2[0] == "(" ? "" : "(");

			this.setValue(str1 + str2);
			this.getInputNode().setSelectionRange(str1.length, str1.length);
		}
	},
	expectOperator(ignoreSel){
		const text = this.getValue();

		if(text[0] == "="){
			let cursor = ignoreSel ? text.length : this.getInputNode().selectionStart;
			while(text[cursor-1] == " ")
				cursor--;
			return text[cursor-1] && text[cursor-1].match(/[+&\-/*=(:,]/);
		}
	},
	setRange(range, replace){
		const cursor = this.getInputNode().selectionStart;
		const formula = this.getValue();

		let str1, str2;

		//check if range was added by click
		if(replace)
			replace = this._update_range && cursor == this._update_range.pos + this._update_range.len;

		const lastPos = replace ? this._update_range.pos : cursor;

		str1 = formula.substring(0, lastPos) + range;
		str2 = formula.substring(cursor);

		this._update_range = {pos: lastPos, len: range.length};

		this.setValue(str1 + str2);
	},
	getCellValue(row, column){
		const master = this.getTopParentView();
		let value = master.getCellValue(row, column);
		if(getType(master, row, column) == "date" && value[0] != "="){
			const format = getDateEditFormat(master, row, column);
			value = format(getJsDateFromExcel(value));
		}
		return value;
	}
}, webix.ui.texthighlight);